import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { Route } from 'vue-router'
import { Store } from 'vuex'
import RootState from './store/state'
import { Actions, Getters } from './store/manager/helpers'
import * as ComponentsHelpers from './store/editor/components/helpers'
import * as CommonHelpers from './store/common/helpers'
import NotificationsVue from './components/notifications/Notifications.vue'
import RedirectToLogin from './components/dialog/RedirectToLogin.vue'
import retryPromise, { createRetryHandler, RetryHandler } from './misc/retry-promise'
import { ErrorStatus } from './store/common/state'
import { isNotFound, isUnauthorized } from './misc/get-status-code'

@Component({
  components: {
    'notifications-panel': NotificationsVue,
    'redirect-dialog': RedirectToLogin
  }
})
export default class ChamomileAppVue extends Vue {
  name: 'ContentsManage'
  $store: Store<RootState>
  $route: Route
  readonly aquariumLoginUrl: string = process.env.AQUARIUM_LOGIN_URL
  isLoading: boolean = true
  retryHandler: RetryHandler = createRetryHandler()

  get siteId(): number {
    return parseInt(this.$route.params['kajiki_id'], 10)
  }

  get errorStatus(): ErrorStatus {
    return CommonHelpers.Getters.errorStatus(this.$store)
  }

  set errorStatus(errorStatus: ErrorStatus) {
    CommonHelpers.Mutations.setErrorStatus(this.$store, errorStatus)
  }

  get accountName(): string {
    return Getters.accountName(this.$store)
  }

  created() {
    this.loadKajiki(async () => {
      // バックアップから復旧する記事があれば、記事データを復旧して記事編集画面に遷移する
      // （初回のロード時に限る）
      return new Promise<void>(async (resolve, reject) => {
        const backedUpSiteId = await ComponentsHelpers.Actions.getBackedUpId(this.$store)
        if (!!backedUpSiteId && !!Getters.kajikiList(this.$store).find(kajiki => kajiki.kajikiId === backedUpSiteId)) {
          // バックアップがある場合、記事編集画面にリダイレクトする
          this.$router.replace({
            name: 'editor-restore-backup',
            params: {
              kajiki_id: backedUpSiteId.toString()
            },
            query: {
              // URLが同一だと更なるURL書き換えに失敗し、その結果 props の書き換えも失敗する。
              // ダミーのパラメータを追加することで、URLの書き換えが成功するようにして、 porps の値が変更できるようにする。
              ts: Date.now().toString()
            }
          }, resolve, () => {
            reject('記事編集画面へのリダイレクトに失敗')
          })
          return
        }
        resolve()
      })
    })
  }

  /**
   * kajikiDBからデータを取得しStoreに保存する
   * @param {() => (void | Promise<any>)} onAfterLoad - kajikiDB を読み込んだ後に実行する処理
   */
  async loadKajiki(onAfterLoad: () => void | Promise<any> = async () => {
  }): Promise<void> {
    const isInitialAccess = this.isLoading
    try {
      await retryPromise<void>((retry, count) => {
        return Actions.loadKajikiData(this.$store, this.siteId)
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
      })
      await onAfterLoad()
      this.isLoading = false
      CommonHelpers.Actions.removeNotificationByType(this.$store, 'connection-error')
    } catch (reason) {
      if (isUnauthorized(reason)) {
        if (isInitialAccess) {
          this.redirectToLogin()
          return
        }
        this.errorStatus = 'session-timeout'
        return
      }

      if (isNotFound(reason)) {
        this.errorStatus = 'not-found'
        return
      }

      if (isInitialAccess) {
        this.errorStatus = 'bad-connection'
        return
      }
      await CommonHelpers.Actions.addNotification(this.$store, {
        type: 'connection-error',
        level: 'warning',
        message: 'データの取得に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    }
  }

  @Watch('siteId')
  async onSiteIdChanged(): Promise<void> {
    await this.loadKajiki()
  }

  @Watch('$route')
  clearNotifications(): void {
    // 画面遷移したらメッセージを全て消す
    CommonHelpers.Actions.clearNotifications(this.$store)
  }

  redirectToLogin(): void {
    window.location.href = this.aquariumLoginUrl
  }
}
